import { useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { useAdminAddQuestionMutation } from '../../../../store/api/apiSlice';
import { readableError } from '../../lib';

interface QuestionFormProps {
    videoId: string;
    courseId: string;
    onDone: () => void;
}

interface OptionDraft {
    text: string;
    correct: boolean;
}

export default function QuestionForm({ videoId, courseId, onDone }: QuestionFormProps) {
    const [addQuestion, { isLoading }] = useAdminAddQuestionMutation();
    const [text, setText] = useState('');
    const [options, setOptions] = useState<OptionDraft[]>([
        { text: '', correct: true },
        { text: '', correct: false },
    ]);
    const [formError, setFormError] = useState('');

    const setCorrect = (index: number) => setOptions((opts) => opts.map((o, i) => ({ ...o, correct: i === index })));

    const updateText = (index: number, value: string) =>
        setOptions((opts) => opts.map((o, i) => (i === index ? { ...o, text: value } : o)));

    const addOption = () => setOptions((opts) => [...opts, { text: '', correct: false }]);

    const removeOption = (index: number) =>
        setOptions((opts) => {
            const next = opts.filter((_, i) => i !== index);
            // Keep exactly one correct option selected.
            if (!next.some((o) => o.correct) && next[0]) next[0].correct = true;
            return next;
        });

    const handleSubmit = async () => {
        setFormError('');
        const filled = options.filter((o) => o.text.trim());
        if (!text.trim()) return setFormError('Question text is required.');
        if (filled.length < 2) return setFormError('Add at least 2 options.');
        if (filled.filter((o) => o.correct).length !== 1) return setFormError('Mark exactly one option correct.');

        try {
            await addQuestion({
                videoId,
                courseId,
                body: {
                    text: text.trim(),
                    options: filled.map((o) => ({ text: o.text.trim(), isCorrect: o.correct })),
                },
            }).unwrap();
            onDone();
        } catch (err) {
            setFormError(readableError(err, 'Could not add the question.'));
        }
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
            <label className="mb-1.5 block text-sm font-medium text-gray-900">Question</label>
            <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="What is the first step on arrival?"
                className="mb-4 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
            />

            <p className="mb-2 text-sm font-medium text-gray-900">Options (select the correct one)</p>
            <div className="space-y-2">
                {options.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <input
                            type="radio"
                            name={`correct-${videoId}`}
                            checked={opt.correct}
                            onChange={() => setCorrect(i)}
                            className="h-4 w-4 accent-[#FF6B35]"
                            aria-label={`Mark option ${i + 1} correct`}
                        />
                        <input
                            value={opt.text}
                            onChange={(e) => updateText(i, e.target.value)}
                            placeholder={`Option ${i + 1}`}
                            className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35]"
                        />
                        {options.length > 2 && (
                            <button
                                type="button"
                                onClick={() => removeOption(i)}
                                aria-label="Remove option"
                                className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                ))}
            </div>

            <button
                type="button"
                onClick={addOption}
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#FF6B35] hover:underline"
            >
                <Plus className="h-4 w-4" /> Add option
            </button>

            {formError && <p className="mt-3 text-sm text-red-600">{formError}</p>}

            <div className="mt-4 flex justify-end gap-2">
                <button
                    type="button"
                    onClick={onDone}
                    className="rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                >
                    {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save question
                </button>
            </div>
        </div>
    );
}
